import React, { useState } from 'react';
import { Fuel, Wrench, CircleDot, BarChart3, Camera, ShieldCheck, Gift, Sparkles, CreditCard } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { EftNotice } from './EftNotice';

const PERKS = [
  { icon: Fuel, text: 'Track petrol, repairs and tyres' },
  { icon: Camera, text: 'Snap a photo of every slip' },
  { icon: BarChart3, text: 'See spending per month, quarter, six months and year' },
  { icon: ShieldCheck, text: 'Your records stay safe, even if a plan ends' },
  { icon: Wrench, text: 'Each vehicle has its own plan' },
  { icon: CircleDot, text: 'Operators can check every driver expense' },
];

const TrialButton = ({ status, onStartTrial, busy }) => {
  const [vehicleId, setVehicleId] = useState('');
  const { trial, role, vehicles } = status;
  if (trial.active) {
    return <p className="text-emerald-200 font-semibold" data-testid="offer-trial-running">Your free trial is running: {trial.days_left} day(s) left.</p>;
  }
  if (trial.used) {
    return <p className="text-emerald-100/80 text-sm" data-testid="offer-trial-used">You have already used your free trial.</p>;
  }
  if (vehicles.length === 0) {
    return <p className="text-emerald-100/80 text-sm" data-testid="offer-no-vehicle">You need a vehicle on your account before you can start the trial.</p>;
  }
  const needsPick = role === 'transport';
  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
      {needsPick && (
        <Select value={vehicleId} onValueChange={setVehicleId}>
          <SelectTrigger className="bg-white text-foreground w-full sm:w-56" data-testid="trial-vehicle-select">
            <SelectValue placeholder="Vehicle you drive" />
          </SelectTrigger>
          <SelectContent>
            {vehicles.map((v) => <SelectItem key={v.vehicle_id} value={v.vehicle_id}>{v.registration_number}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      <Button size="lg" disabled={busy || (needsPick && !vehicleId)} onClick={() => onStartTrial(vehicleId || null)}
        className="rounded-full bg-amber-400 text-emerald-950 hover:bg-amber-300 font-bold shadow-lg" data-testid="start-free-trial-btn">
        <Gift className="w-5 h-5 mr-2" /> Try Finance Tracker free for 1 month
      </Button>
    </div>
  );
};

export const OfferPanel = ({ status, onStartTrial, onPay, busy }) => (
  <section className="relative overflow-hidden rounded-3xl bg-emerald-950 text-white p-6 sm:p-10" data-testid="finance-offer-panel">
    <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-emerald-700/40 blur-2xl" />
    <div className="absolute right-6 bottom-6 text-[9rem] leading-none font-heading font-black text-emerald-800/40 select-none hidden lg:block">R199</div>
    <div className="relative max-w-3xl space-y-5">
      <p className="inline-flex items-center gap-2 rounded-full bg-amber-400/15 text-amber-300 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
        <Sparkles className="w-3.5 h-3.5" /> Finance Tracker
      </p>
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold leading-tight" data-testid="offer-headline">Know where every rand goes.</h2>
      <p className="text-base md:text-lg text-emerald-50">One payment of <strong className="text-amber-300">R199 per vehicle</strong> covers your whole year.</p>
      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        <p className="rounded-2xl bg-white/10 p-4">That's about <strong>R20 a month</strong>, and you pay it once.</p>
        <p className="rounded-2xl bg-white/10 p-4"><strong>2 months free:</strong> your first month is a free trial, and one more month is on us during your year.</p>
      </div>
      <p className="text-emerald-100">Track petrol, repairs and tyres. See your spending per month, quarter, six months and year.</p>
      <div>
        <p className="text-xs uppercase tracking-wider text-emerald-300 mb-2 font-semibold">What you get</p>
        <ul className="grid sm:grid-cols-2 gap-2" data-testid="offer-perks">
          {PERKS.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-center gap-3 text-sm">
              <span className="w-8 h-8 rounded-lg bg-emerald-800 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-amber-300" /></span>
              {text}
            </li>
          ))}
        </ul>
      </div>
      <div className="flex flex-col gap-3 pt-2">
        <TrialButton status={status} onStartTrial={onStartTrial} busy={busy} />
        {status.vehicles.length > 0 && (
          <Button variant="outline" onClick={onPay} className="rounded-full bg-transparent border-white/40 text-white hover:bg-white/10 w-full sm:w-fit" data-testid="offer-pay-btn">
            <CreditCard className="w-4 h-4 mr-2" /> Pay R199 per vehicle by EFT
          </Button>
        )}
      </div>
      <EftNotice compact />
    </div>
  </section>
);
